import * as moment from 'moment';

import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  ViewChild,
} from '@angular/core'

import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR
} from '@angular/forms'

interface Daterange {
  startDate: Date
  endDate: Date,
  range?: string
}

export const DATERANGE_PICKER_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => DaterangePickerComponent),
  multi: true,
}

@Component({
  selector: 'daterange-picker',
  styles: [
    `
    .ui.input input {
      min-width: 220px;
    }
    `
  ],
  templateUrl: './daterange-picker.component.html',
  providers: [
    DATERANGE_PICKER_VALUE_ACCESSOR
  ]
})
export class DaterangePickerComponent implements AfterViewInit, ControlValueAccessor  {
  @ViewChild('elementRef') elementRef: ElementRef
  @Input() format = 'MMM D, YYYY'
  @Input() min: Date
  @Input() max: Date
  @Input() opens: string = 'right'
  @Input() disabled: boolean = false
  @Input() ranges: any = undefined
  @Input() editable
  @Output() valueChange: EventEmitter<Daterange> = new EventEmitter()
  value: Daterange

  $element: any
  onChangeCallback: any

  ngAfterViewInit() {
    this.$element = $(this.elementRef.nativeElement)

    let options = {
      linkedCalendars: false,
      buttonClasses: 'ui button fluid',
      applyClass: 'primary',
      autoUpdateInput: false,
      template: '',
      locale: {
        format: this.format,
      },
      minDate: this.min,
      maxDate: this.max,
      opens: this.opens,
      updateTextOnHover: false,
      alwaysShowCalendars: true,
      showCustomRangeLabel: true,
    }

    if (this.ranges) {
      options = Object.assign(options, {ranges: this.ranges})
    }

    if (this.value) {
      options['startDate'] = this.value.startDate
      options['endDate'] = this.value.endDate
    }

    options.template = `
      <div class="daterangepicker dropdown-menu ui compact segment grid" style="display: none;">
        <div class="two column row" ${this.ranges ? 'style="width: 735px !important;"' : ''}>
          <div class="column calendar left">
            <div class="daterangepicker_input" ${this.ranges ? 'style="width: 100%; position: initial;"' : ''}>
              <div class="ui left icon input fluid">
                <input type="text" name="daterangepicker_start" value="" readonly/>
                <i class="calendar icon"></i>
              </div>
            </div>
            <div class="calendar-table"></div>
          </div>
          <div class="column calendar right">
            <div class="daterangepicker_input" ${this.ranges ? 'style="width: 100%; position: initial;"' : ''}>
              <div class="ui left icon input fluid">
                <input type="text" name="daterangepicker_end" value="" readonly/>
                <i class="calendar icon"></i>
              </div>
            </div>
            <div class="calendar-table"></div>
          </div>
          <div id="daterangepicker-ranges" class="column ranges">
            <div class="range_inputs">
              <button class="applyBtn" disabled="disabled" type="button"></button>
            </div>
          </div>
        </div>
      </div>`

    this.$element.daterangepicker(options, (start, end, dateRange) => {
      this.value = {
          startDate: start.toDate(),
          endDate: end.toDate(),
          range: dateRange
        }
      this.setStringValue(dateRange)

      if (typeof this.onChangeCallback === 'function') {
        this.onChangeCallback(this.value)
      }
      this.valueChange.emit(this.value)
    })

    if (this.value) {
      this.setStringValue(this.value.range)
    }

    this.addEventListeners()
  }

  addEventListeners() {
    this.$element.on('show.daterangepicker', (ev, picker) => {
      picker.elementChanged()
    })
  }

  setStringValue(dateRange = undefined) {
    if (dateRange == 'Custom Range') { dateRange = undefined }
    const picker = this.$element.data('daterangepicker')
    const format = picker.locale.format
    if (picker && picker.startDate && picker.endDate) {
      const stringVal = moment(this.value.startDate).format(format) + ' - ' + moment(this.value.endDate).format(format)
      this.$element.val(dateRange || stringVal)
    }
  }

  writeValue(value: Daterange) {
    this.value = value
    if (!value) {
      if (this.$element) {
        this.$element.val('');
      }
      return
    }

    if (this.value && this.$element) {
      const picker = this.$element.data('daterangepicker')

      picker.setStartDate(this.value.startDate)
      picker.setEndDate(this.value.endDate)

      this.setStringValue(this.value.range)
    }
  }

  setDisabledState(disabled) {
    this.disabled = disabled
  }

  registerOnChange(fn: any) {
    this.onChangeCallback = fn
  }

  registerOnTouched(fn: any) { }
}